import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';
import { isCompanionAppVersionFormatValid } from '../../../helpers/checkCompanionVersionForValidity';

import { DistributionService } from './distribution.service';

@Component({
  selector: 'remote-update-domain-app-distribution',
  templateUrl: './distribution.html',
  styleUrls: ['./distribution.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonToggleModule,
    FormsModule,
    MatCheckboxModule,
    MatInputModule
  ]
})
export class Distribution implements OnInit {
  @Input() currentSection = '';
  private store = inject(StepperFormStore);
  private versionRegex = /^\d+(\.\d+){1,4}$/;
  private readonly distributionService = inject(DistributionService);
  readonly companionApps: {
    name: string;
    appleMinimumVersion: string;
    androidMinimumVersion: string;
  }[] = [];
  distributionForm = new FormGroup({
    access: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    companionApps: new FormArray<FormGroup>([], { validators: [Validators.required] })
  });

  async ngOnInit(): Promise<void> {
    const distributionData = this.store.distribution();

    const apps = await this.distributionService.getAllSupportedApps();
    this.companionApps.push(...apps);
    this.buildCompanionApps(apps);

    if (distributionData.access) {
      this.distributionForm.patchValue({ access: distributionData.access.trim() });
    }

    if (
      distributionData.supportedCompanionApps &&
      distributionData.supportedCompanionApps.length > 0
    ) {
      distributionData.supportedCompanionApps.forEach(app => {
        const appGroup = this.getAppGroup(app.name);
        if (appGroup) {
          appGroup.patchValue(
            {
              enabled: true,
              appleMinimumVersion: app.appleMinimumVersion || '',
              androidMinimumVersion: app.androidMinimumVersion || ''
            },
            { emitEvent: false }
          );
          appGroup.get('appleMinimumVersion')?.enable({ emitEvent: false });
          appGroup.get('androidMinimumVersion')?.enable({ emitEvent: false });
        }
      });
    }

    this.updateDistributionStateFromForm();

    if (this.store.isEditingFirmwarePackage() && distributionData.isValid) {
      this.store.updateDistribution(
        {
          access: distributionData.access,
          supportedCompanionApps: distributionData.supportedCompanionApps,
          isValid: distributionData.isValid
        },
        ''
      );
    }
  }

  private buildCompanionApps(
    appsFromBackend: { name: string; appleMinimumVersion: string; androidMinimumVersion: string }[]
  ) {
    const appsArray = this.distributionForm.get('companionApps') as FormArray;

    appsFromBackend.forEach(app => {
      const appGroup = new FormGroup({
        originalName: new FormControl(app.name, { nonNullable: true }),
        enabled: new FormControl(false, { nonNullable: true }),
        appleMinimumVersion: new FormControl({ value: '', disabled: true }),
        androidMinimumVersion: new FormControl({ value: '', disabled: true }),
        backendAppleMin: new FormControl(app.appleMinimumVersion),
        backendAndroidMin: new FormControl(app.androidMinimumVersion)
      });

      appGroup.get('enabled')?.valueChanges.subscribe(enabled => {
        this.enableVersionsAndClearOnDisable(appGroup, enabled);
      });

      appsArray.push(appGroup);
    });
  }
  // ───────────end──────────────────────
  private getAppGroup(appName: string): FormGroup | null {
    return (
      (this.companionAppsArray.controls.find(
        ctrl => ctrl.get('originalName')?.value === appName
      ) as FormGroup) ?? null
    );
  }

  private getControlName(platform: 'iOS' | 'Android'): string {
    return platform === 'iOS' ? 'appleMinimumVersion' : 'androidMinimumVersion';
  }

  private getBackendMinControl(platform: 'iOS' | 'Android'): string {
    return platform === 'iOS' ? 'backendAppleMin' : 'backendAndroidMin';
  }

  get companionAppsArray() {
    return this.distributionForm.get('companionApps') as FormArray<FormGroup>;
  }

  get showAccessError(): boolean | undefined {
    return this.distributionForm.get('access')?.invalid && !!this.error;
  }

  get error(): string | undefined {
    return this.store.errors().distribution;
  }

  requireAtLeastOneEnabledCompanionApp(): boolean {
    return Object.values(this.companionAppsArray.controls).some(
      appForm => appForm.get('enabled')?.value === true
    );
  }

  enableVersionsAndClearOnDisable(group: FormGroup, enabled: boolean) {
    const ios = group.get('appleMinimumVersion');
    const android = group.get('androidMinimumVersion');

    if (enabled) {
      ios?.enable({ emitEvent: false });
      android?.enable({ emitEvent: false });
    } else {
      ios?.reset('');
      android?.reset('');
      ios?.disable({ emitEvent: false });
      android?.disable({ emitEvent: false });
    }
  }

  getCompanionAppVersionError(appName: string, platform: 'iOS' | 'Android'): boolean {
    const appGroup = this.getAppGroup(appName);
    if (!appGroup) return false;

    const value = appGroup.get(this.getControlName(platform))?.value ?? '';
    return (appGroup.get('enabled')?.value ?? false) && value.trim() === '';
  }

  isBelowGeneralMinVersion(appName: string, platform: 'iOS' | 'Android'): boolean {
    const appGroup = this.getAppGroup(appName);
    if (!appGroup || !appGroup.get('enabled')?.value) return false;

    const value = appGroup.get(this.getControlName(platform))?.value?.trim();
    const backendMin = appGroup.get(this.getBackendMinControl(platform))?.value;

    if (!value || !backendMin || !this.versionRegex.test(value)) return false;

    return !isCompanionAppVersionFormatValid(value, backendMin);
  }

  isInvalidCompanionAppVersion(appName: string, platform: 'iOS' | 'Android'): boolean {
    const appGroup = this.getAppGroup(appName);
    if (!appGroup || !appGroup.get('enabled')?.value) return false;

    const value = appGroup.get(this.getControlName(platform))?.value?.trim();
    if (!value) return false;

    return !this.versionRegex.test(value);
  }

  checkVersionInputValidity(appName: string, platform: 'iOS' | 'Android'): boolean {
    if (this.getCompanionAppVersionError(appName, platform)) return false;

    const appGroup = this.getAppGroup(appName);
    const value = appGroup?.get(this.getControlName(platform))?.value?.trim() ?? '';
    const backendMin = appGroup?.get(this.getBackendMinControl(platform))?.value;

    if (!this.versionRegex.test(value)) {
      this.store.setHasInvalidCompanionAppVersion(true);
      return false;
    }

    if (backendMin && !isCompanionAppVersionFormatValid(value, backendMin)) {
      this.store.setIsBelowGeneralMinVersion(true);
      return false;
    }

    return true;
  }

  private updateDistributionStateFromForm() {
    this.distributionForm.valueChanges.subscribe(() => {
      this.store.setIsCompanionAppVersionEmpty(false);
      this.store.setHasInvalidCompanionAppVersion(false);
      this.store.setIsBelowGeneralMinVersion(false);

      const access = this.distributionForm.get('access')?.value ?? '';
      const enabledGroups = Object.values(this.companionAppsArray.controls).filter(
        group => group.get('enabled')?.value
      );

      const hasEmptyCompanionAppVersion = enabledGroups.some(group => {
        const ios = group.get('appleMinimumVersion')?.value?.trim() ?? '';
        const android = group.get('androidMinimumVersion')?.value?.trim() ?? '';
        return ios === '' || android === '';
      });

      this.store.setIsCompanionAppVersionEmpty(hasEmptyCompanionAppVersion);

      const allAppsValid =
        !hasEmptyCompanionAppVersion &&
        enabledGroups.every(group => {
          const appName = group.get('originalName')?.value;
          if (!appName) return false;
          return (
            this.checkVersionInputValidity(appName, 'iOS') &&
            this.checkVersionInputValidity(appName, 'Android')
          );
        });

      const supportedCompanionApps = enabledGroups.map(group => ({
        name: group.get('originalName')?.value,
        appleMinimumVersion: group.get('appleMinimumVersion')?.value?.trim() || undefined,
        androidMinimumVersion: group.get('androidMinimumVersion')?.value?.trim() || undefined
      }));

      const isValid = access.trim().length > 0 && supportedCompanionApps.length > 0 && allAppsValid;

      if (isValid) this.store.clearError('distribution');

      this.store.updateDistribution({ access, supportedCompanionApps, isValid }, '');
    });
  }
}
